"use client";

import { useEffect, useState } from "react";
import Link from "next/link";
import { useDispatch, useSelector } from "react-redux";
import useUtility from "@/app/_hooks/useUtility";
import { getUser } from "@/lib/helpers";
import { setUserData } from "@/store/userSlice";
import { Logo } from "./Logo";
import { usePathname } from "next/navigation";

export default function Navbar() {
  const pathname = usePathname();
  const dispatch = useDispatch();
  const { trans } = useUtility();

  const { data: userData } = useSelector((state) => state.user);
  const customPageData = useSelector((state) => state.customPage.data);

  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [isFixed, setIsFixed] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(null); // "product", "company", or null

  const isHome = pathname === "/";

  // Set user data on initial mount
  useEffect(() => {
    dispatch(setUserData(getUser()));
  }, [dispatch]);

  // Update login state based on userData
  useEffect(() => {
    setIsLoggedIn(!!userData);
  }, [userData]);

  useEffect(() => {
    const navItems = document.querySelectorAll(".navbar-nav .nav-item");
    navItems.forEach((el) => {
      const handleHover = () => {
        el.classList.add("hover");
      };

      const mouseleave = () => {
        el.classList.remove("hover");
      };

      const handleClick = (e) => {
        if (e.target.closest(".dropdown-menu__link")) {
          el.classList.remove("hover");
        }
      };

      el.addEventListener("mouseenter", handleHover);
      el.addEventListener("mouseleave", mouseleave);
      el.addEventListener("click", handleClick);

      // Cleanup
      return () => {
        el.removeEventListener("mouseenter", handleHover);
        el.removeEventListener("mouseleave", mouseleave);
        el.removeEventListener("click", handleClick);
      };
    });

    const menuLinks = document.querySelectorAll(
      ".navbar-nav .dropdown-menu__link",
    );
    menuLinks.forEach((el) => {
      const handleClick = (e) => {
        setIsMenuOpen(false);
      };

      el.addEventListener("click", handleClick);

      // Cleanup
      return () => {
        el.removeEventListener("click", handleClick);
      };
    });

    // Add scroll listener for sticky header
    const handleScroll = () => {
      setIsFixed(window.scrollY > 0);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    setTimeout(() => {
      const header = document?.getElementById("header");

      if (pathname == "/") {
        header?.classList?.add("transparent-header");
      } else {
        header?.classList?.remove("transparent-header");
      }
    }, 500);
  }, [pathname]);

  // Toggle mobile menu
  const toggleMenu = () => {
    setIsMenuOpen((prev) => !prev);
    document.body.classList.toggle("scroll-hide-sm");
  };

  const closeMenu = () => {
    setIsMenuOpen(false);
    document.body.classList.remove("scroll-hide-sm");
  };

  // Toggle dropdown (used for product/company menus)
  const toggleDropdown = (key) => {
    setOpenDropdown((prev) => (prev === key ? null : key));
  };

  return (
    <>
      {/* Body Overlay */}
      <div
        className={`body-overlay ${isMenuOpen ? "show" : ""}`}
        onClick={closeMenu}
      />

      <header
        className={`header ${isFixed ? "fixed-header" : ""} transparent-header`}
        id="header"
        style={
          isFixed
            ? { width: "100%" }
            : isHome
              ? { background: "transparent", position: "absolute", top: "4px" }
              : { background: "#fff", position: "relative", top: "4px" }
        }
      >
        <div className="container" style={{ paddingTop: "8px", paddingBottom: "8px" }}>
          <nav className="navbar navbar-expand-lg navbar-light">
            <button
              className="navbar-toggler header-button"
              type="button"
              data-bs-toggle="collapse"
              data-bs-target="#navbarSupportedContent"
              aria-controls="navbarSupportedContent"
              aria-expanded={isMenuOpen}
              onClick={toggleMenu}
              aria-label="Toggle navigation"
            >
              <span id="hiddenNav" className="d-inline-flex">
                <i className="las la-bars"></i>
              </span>
            </button>

            <Logo className="navbar-brand logo" />

            <div
              className={`navbar-collapse collapse ${isMenuOpen ? "show" : ""}`}
              id="navbarSupportedContent"
            >
              <ul className="navbar-nav nav-menu align-items-lg-center w-100">
                {/* Example nav item:  */}
                <li
                  className={`nav-item has-dropdown ${openDropdown === "product" ? "show" : ""
                    }`}
                >
                  <a
                    className={`nav-link ${openDropdown === "product" ? "show" : ""
                      }`}
                    href="#"
                    role="button"
                    data-bs-toggle="dropdown"
                    aria-expanded={openDropdown === "product"}
                    onClick={() => toggleDropdown("product")}
                  >
                    <span className="nav-link__text">{trans("Products")}</span>
                    <span
                      className="nav-link__icon"
                      style={{ marginTop: "-5px" }}
                    >
                      <svg
                        width="17"
                        height="16"
                        viewBox="0 0 17 16"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M14.1401 5.96875L9.79341 10.3154C9.28008 10.8288 8.44008 10.8288 7.92675 10.3154L3.58008 5.96875"
                          stroke="#212121"
                          strokeMiterlimit="10"
                          stroke-linecap="round"
                          stroke-linejoin="round"
                        />
                      </svg>
                    </span>
                  </a>

                  <ul
                    className={`dropdown-menu mega-menu two ${openDropdown === "product" ? "show" : ""
                      }`}
                    data-bs-popper={
                      openDropdown === "product" ? "static" : undefined
                    }
                  >
                    <span className="dropdown-menu__clip-path-shape">
                      <svg
                        viewBox="0 0 30 22"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M12.6 1.2C13.8 -0.4 16.2 -0.4 17.4 1.2L29.4 17.2C30.8833 19.1777 29.4721 22 27 22H3C0.527865 22 -0.883281 19.1777 0.600001 17.2L12.6 1.2Z"
                          fill="currentColor"
                        />
                      </svg>
                    </span>
                    <li className="dropdown-menu__item">
                      <ul>
                        <li>
                          <Link
                            className="dropdown-menu__link"
                            href="/user/send-money/step-1"
                          >
                            <span className="dropdown-menu__icon">
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                viewBox="0 0 32 33"
                                fill="none"
                              >
                                <path
                                  d="M19.6805 16.502H9.10984C9.10984 16.1353 9.03384 15.7686 8.88317 15.4246L5.71384 8.25398C4.70051 5.96065 7.12451 3.66465 9.35917 4.79931L27.7338 14.1246C29.6805 15.1113 29.6805 17.8926 27.7338 18.8793L9.36051 28.2046C7.12451 29.3393 4.70051 27.042 5.71384 24.75L8.88051 17.5793C9.03018 17.2399 9.10738 16.8729 9.10717 16.502"
                                  stroke="currentColor"
                                  strokeWidth="2"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                />
                              </svg>
                            </span>
                            <div className="dropdown-menu__content">
                              <h6 className="dropdown-menu__title">
                                {trans("Money Transfer")}
                              </h6>
                              <p className="dropdown-menu__desc">
                                {trans(
                                  "Send money across Africa quickly and securely",
                                )}
                              </p>
                            </div>
                          </Link>
                        </li>
                        <li>
                          <Link
                            className="dropdown-menu__link"
                            href="/marketplace"
                          >
                            <span className="dropdown-menu__icon">
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                viewBox="0 0 37 37"
                                fill="none"
                              >
                                <path
                                  d="M22.6627 35.0723H14.3377C6.72188 35.0723 3.48438 31.8194 3.48438 24.219V17.2969C3.48438 16.6648 4.00854 16.1406 4.64063 16.1406C5.27271 16.1406 5.79688 16.6648 5.79688 17.2969V24.219C5.79688 30.5244 8.03229 32.7598 14.3377 32.7598H22.6473C28.9527 32.7598 31.1881 30.5244 31.1881 24.219V17.2969C31.1881 16.6648 31.7123 16.1406 32.3444 16.1406C32.9765 16.1406 33.5006 16.6648 33.5006 17.2969V24.219C33.516 31.8194 30.2631 35.0723 22.6627 35.0723Z"
                                  fill="currentColor"
                                />
                                <path
                                  d="M18.5009 19.6589C16.8051 19.6589 15.2634 18.9959 14.1689 17.778C13.0743 16.5601 12.5655 14.9722 12.7351 13.2764L13.768 2.97802C13.8297 2.39219 14.323 1.92969 14.9243 1.92969H22.1239C22.7251 1.92969 23.2184 2.37677 23.2801 2.97802L24.313 13.2764C24.4826 14.9722 23.9739 16.5601 22.8793 17.778C21.7384 18.9959 20.1968 19.6589 18.5009 19.6589ZM15.9572 4.24219L15.0322 13.5076C14.9243 14.5405 15.2326 15.5118 15.8801 16.2209C17.1905 17.6701 19.8114 17.6701 21.1218 16.2209C21.7693 15.4964 22.0776 14.5251 21.9697 13.5076L21.0447 4.24219H15.9572Z"
                                  fill="currentColor"
                                />
                                <path
                                  d="M28.2265 19.6589C25.097 19.6589 22.3065 17.1305 21.9828 14.0164L20.9036 3.20927C20.8728 2.88552 20.9807 2.56177 21.1965 2.3151C21.4124 2.06844 21.7207 1.92969 22.0599 1.92969H26.762C31.2945 1.92969 33.4065 3.82594 34.0386 8.48177L34.4703 12.7676C34.6553 14.5868 34.1003 16.3134 32.9132 17.6239C31.7261 18.9343 30.0611 19.6589 28.2265 19.6589ZM23.3395 4.24219L24.2953 13.7851C24.4957 15.7122 26.284 17.3464 28.2265 17.3464C29.3982 17.3464 30.4465 16.8993 31.202 16.0822C31.942 15.2651 32.2811 14.1705 32.1732 12.9989L31.7415 8.75927C31.2636 5.2751 30.1382 4.24219 26.762 4.24219H23.3395V4.24219Z"
                                  fill="currentColor"
                                />
                                <path
                                  d="M8.69662 19.6589C6.86204 19.6589 5.19704 18.9343 4.00995 17.6239C2.82287 16.3134 2.26787 14.5868 2.45287 12.7676L2.86912 8.52802C3.51662 3.82594 5.6287 1.92969 10.1612 1.92969H14.8633C15.187 1.92969 15.4954 2.06844 15.7266 2.3151C15.9579 2.56177 16.0504 2.88552 16.0195 3.20927L14.9404 14.0164C14.6166 17.1305 11.8262 19.6589 8.69662 19.6589ZM10.1612 4.24219C6.78495 4.24219 5.65954 5.25969 5.1662 8.7901L4.74995 12.9989C4.62662 14.1705 4.9812 15.2651 5.7212 16.0822C6.4612 16.8993 7.50954 17.3464 8.69662 17.3464C10.6391 17.3464 12.4429 15.7122 12.6279 13.7851L13.5837 4.24219H10.1612V4.24219Z"
                                  fill="currentColor"
                                />
                                <path
                                  d="M22.3529 35.0755H14.6445C14.0124 35.0755 13.4883 34.5514 13.4883 33.9193V30.0651C13.4883 26.8276 15.2612 25.0547 18.4987 25.0547C21.7362 25.0547 23.5091 26.8276 23.5091 30.0651V33.9193C23.5091 34.5514 22.9849 35.0755 22.3529 35.0755ZM15.8008 32.763H21.1966V30.0651C21.1966 28.1226 20.4412 27.3672 18.4987 27.3672C16.5562 27.3672 15.8008 28.1226 15.8008 30.0651V32.763Z"
                                  fill="currentColor"
                                />
                              </svg>
                            </span>
                            <div className="dropdown-menu__content">
                              <h6 className="dropdown-menu__title">
                                {trans("Marketplace")}
                              </h6>
                              <p className="dropdown-menu__desc">
                                {trans("Explore our financial services hub.")}
                              </p>
                            </div>
                          </Link>
                        </li>
                        <li>
                          <Link className="dropdown-menu__link" href="/convert">
                            <span className="dropdown-menu__icon">
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                viewBox="0 0 37 37"
                                fill="none"
                              >
                                <path
                                  d="M4.79328 25.3476V33.0559Z"
                                  fill="currentColor"
                                />
                                <path
                                  d="M33.9154 18.5026C33.9154 27.0126 27.0087 33.9193 18.4987 33.9193C9.9887 33.9193 4.79328 25.3476 4.79328 25.3476M4.79328 25.3476H11.7616M4.79328 25.3476V33.0559M3.08203 18.5026C3.08203 9.9926 9.92703 3.08594 18.4987 3.08594C28.7816 3.08594 33.9154 11.6576 33.9154 11.6576M33.9154 11.6576V3.94927M33.9154 11.6576H27.0704"
                                  stroke="currentColor"
                                  strokeWidth="1.5"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                />
                              </svg>
                            </span>
                            <div className="dropdown-menu__content">
                              <h6 className="dropdown-menu__title">
                                {trans("Currency Conversion")}
                              </h6>
                              <p className="dropdown-menu__desc">
                                {trans("Easily convert between currencies.")}
                              </p>
                            </div>
                          </Link>
                        </li>
                      </ul>
                    </li>
                    <li className="dropdown-menu__item">
                      <ul>
                        <li>
                          <Link
                            className="dropdown-menu__link"
                            href="/pages/wallet"
                          >
                            <span className="dropdown-menu__icon">
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                viewBox="0 0 37 37"
                                fill="none"
                              >
                                <path
                                  d="M27.3505 35.0703H9.65214C5.81339 35.0703 2.69922 31.9561 2.69922 28.1174V17.742C2.69922 13.9032 5.81339 10.7891 9.65214 10.7891H27.3505C31.1892 10.7891 34.3034 13.9032 34.3034 17.742V19.962C34.3034 20.5941 33.7792 21.1182 33.1471 21.1182H30.033C29.4934 21.1182 29.0001 21.3187 28.6455 21.6887L28.6301 21.7041C28.1984 22.1203 27.998 22.6907 28.0442 23.2765C28.1367 24.294 29.108 25.1111 30.218 25.1111H33.1471C33.7792 25.1111 34.3034 25.6353 34.3034 26.2674V28.1019C34.3034 31.9561 31.1892 35.0703 27.3505 35.0703ZM9.65214 13.1016C7.09297 13.1016 5.01172 15.1828 5.01172 17.742V28.1174C5.01172 30.6765 7.09297 32.7578 9.65214 32.7578H27.3505C29.9096 32.7578 31.9909 30.6765 31.9909 28.1174V27.439H30.218C27.8901 27.439 25.9167 25.7124 25.7317 23.4924C25.6084 22.2282 26.0709 20.9795 26.9959 20.0699C27.7976 19.2528 28.8767 18.8057 30.033 18.8057H31.9909V17.742C31.9909 15.1828 29.9096 13.1016 27.3505 13.1016H9.65214V13.1016Z"
                                  fill="currentColor"
                                />
                                <path
                                  d="M3.85547 20.289C3.22339 20.289 2.69922 19.7648 2.69922 19.1327V12.0874C2.69922 9.79033 4.14839 7.70899 6.2913 6.89191L18.5321 2.26691C19.7963 1.78899 21.1992 1.95865 22.2938 2.72948C23.4038 3.50032 24.0513 4.74906 24.0513 6.09031V11.9486C24.0513 12.5807 23.5271 13.1049 22.8951 13.1049C22.263 13.1049 21.7388 12.5807 21.7388 11.9486V6.09031C21.7388 5.50447 21.4613 4.96487 20.968 4.6257C20.4746 4.28654 19.8888 4.20945 19.3338 4.42528L7.09297 9.05028C5.84422 9.5282 4.9963 10.7462 4.9963 12.0874V19.1327C5.01172 19.7802 4.48755 20.289 3.85547 20.289Z"
                                  fill="currentColor"
                                />
                                <path
                                  d="M30.2168 27.438C27.8889 27.438 25.9156 25.7113 25.7306 23.4913C25.6072 22.2117 26.0697 20.963 26.9947 20.0534C27.781 19.2518 28.8601 18.8047 30.0164 18.8047H33.2231C34.7493 18.8509 35.921 20.0534 35.921 21.5334V24.7093C35.921 26.1893 34.7493 27.3917 33.2693 27.438H30.2168ZM33.1922 21.1172H30.0318C29.4922 21.1172 28.9989 21.3176 28.6443 21.6876C28.1972 22.1193 27.9814 22.7051 28.0431 23.2909C28.1356 24.3084 29.1068 25.1255 30.2168 25.1255H33.2385C33.4389 25.1255 33.6239 24.9405 33.6239 24.7093V21.5334C33.6239 21.3021 33.4389 21.1326 33.1922 21.1172Z"
                                  fill="currentColor"
                                />
                                <path
                                  d="M21.5846 19.6562H10.793C10.1609 19.6562 9.63672 19.1321 9.63672 18.5C9.63672 17.8679 10.1609 17.3438 10.793 17.3438H21.5846C22.2167 17.3438 22.7409 17.8679 22.7409 18.5C22.7409 19.1321 22.2167 19.6562 21.5846 19.6562Z"
                                  fill="currentColor"
                                />
                              </svg>
                            </span>
                            <div className="dropdown-menu__content">
                              <h6 className="dropdown-menu__title">
                                {trans("Wallet")}
                              </h6>
                              <p className="dropdown-menu__desc">
                                {trans(
                                  "Safely store and manage your digital assets.",
                                )}
                              </p>
                            </div>
                          </Link>
                        </li>
                        <li>
                          <Link
                            className="dropdown-menu__link"
                            href="/exchange-rate"
                          >
                            <span className="dropdown-menu__icon">
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                viewBox="0 0 37 38"
                                fill="none"
                              >
                                <path
                                  d="M23.879 32.5011C23.586 32.5011 23.2931 32.3932 23.0619 32.162C22.6148 31.7149 22.6148 30.9749 23.0619 30.5278L30.7856 22.8041C31.2327 22.357 31.9727 22.357 32.4198 22.8041C32.8669 23.2511 32.8669 23.9911 32.4198 24.4382L24.696 32.162C24.4648 32.3778 24.1719 32.5011 23.879 32.5011Z"
                                  fill="currentColor"
                                />
                                <path
                                  d="M31.6029 24.7656H5.39453C4.76245 24.7656 4.23828 24.2415 4.23828 23.6094C4.23828 22.9773 4.76245 22.4531 5.39453 22.4531H31.6029C32.2349 22.4531 32.7591 22.9773 32.7591 23.6094C32.7591 24.2415 32.2349 24.7656 31.6029 24.7656Z"
                                  fill="currentColor"
                                />
                                <path
                                  d="M5.39458 15.548C5.10167 15.548 4.80875 15.4401 4.5775 15.2089C4.13042 14.7618 4.13042 14.0218 4.5775 13.5747L12.3012 5.85094C12.7483 5.40385 13.4883 5.40385 13.9354 5.85094C14.3825 6.29802 14.3825 7.03802 13.9354 7.4851L6.21167 15.2089C5.99583 15.4247 5.6875 15.548 5.39458 15.548Z"
                                  fill="currentColor"
                                />
                                <path
                                  d="M31.6029 15.5469H5.39453C4.76245 15.5469 4.23828 15.0227 4.23828 14.3906C4.23828 13.7585 4.76245 13.2344 5.39453 13.2344H31.6029C32.2349 13.2344 32.7591 13.7585 32.7591 14.3906C32.7591 15.0227 32.2349 15.5469 31.6029 15.5469Z"
                                  fill="currentColor"
                                />
                              </svg>
                            </span>
                            <div className="dropdown-menu__content">
                              <h6 className="dropdown-menu__title">
                                {trans("Exchange Rate")}
                              </h6>
                              <p className="dropdown-menu__desc">
                                {trans("Get real-time currency rates.")}
                              </p>
                            </div>
                          </Link>
                        </li>
                        <li>
                          <Link className="dropdown-menu__link" href="/explore">
                            <span className="dropdown-menu__icon">
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                viewBox="0 0 37 37"
                                fill="none"
                              >
                                <path
                                  d="M8.53878 28.4612L21.5052 21.5052L28.4612 8.53878L15.4968 15.4968L8.53878 28.4612ZM18.5 20.5556C17.9176 20.5556 17.4297 20.3582 17.0364 19.9636C16.6431 19.5689 16.4458 19.081 16.4444 18.5C16.4431 17.919 16.6404 17.4311 17.0364 17.0364C17.4325 16.6418 17.9203 16.4444 18.5 16.4444C19.0797 16.4444 19.5682 16.6418 19.9656 17.0364C20.363 17.4311 20.5597 17.919 20.5556 18.5C20.5514 19.081 20.3541 19.5696 19.9636 19.9656C19.573 20.3616 19.0851 20.5583 18.5 20.5556ZM18.5062 37C15.9477 37 13.5427 36.5149 11.2912 35.5447C9.03965 34.5731 7.0807 33.2548 5.41433 31.5898C3.74796 29.9248 2.42898 27.9679 1.45739 25.7191C0.485797 23.4703 0 21.066 0 18.5062C0 15.9463 0.485797 13.5413 1.45739 11.2912C2.42761 9.03965 3.74385 7.0807 5.40611 5.41433C7.06837 3.74796 9.02595 2.42898 11.2788 1.45739C13.5317 0.485796 15.9367 0 18.4938 0C21.0509 0 23.4559 0.485796 25.7088 1.45739C27.9604 2.42761 29.9193 3.74454 31.5857 5.40817C33.252 7.0718 34.571 9.02937 35.5426 11.2809C36.5142 13.5324 37 15.9367 37 18.4938C37 21.0509 36.5149 23.4559 35.5447 25.7088C34.5744 27.9617 33.2561 29.9207 31.5898 31.5857C29.9234 33.2507 27.9665 34.5696 25.7191 35.5426C23.4717 36.5156 21.0674 37.0014 18.5062 37ZM18.5 34.9444C23.0907 34.9444 26.9792 33.3514 30.1653 30.1653C33.3514 26.9792 34.9444 23.0907 34.9444 18.5C34.9444 13.9093 33.3514 10.0208 30.1653 6.83472C26.9792 3.64861 23.0907 2.05556 18.5 2.05556C13.9093 2.05556 10.0208 3.64861 6.83472 6.83472C3.64861 10.0208 2.05556 13.9093 2.05556 18.5C2.05556 23.0907 3.64861 26.9792 6.83472 30.1653C10.0208 33.3514 13.9093 34.9444 18.5 34.9444Z"
                                  fill="currentColor"
                                />
                              </svg>
                            </span>
                            <div className="dropdown-menu__content">
                              <h6 className="dropdown-menu__title">
                                {trans("Explore")}
                              </h6>
                              <p className="dropdown-menu__desc">
                                {trans("Discover all our financial solutions")}
                              </p>
                            </div>
                          </Link>
                        </li>
                      </ul>
                    </li>
                    <li className="dropdown-menu__item">
                      <ul>
                        <li>
                          <Link
                            className="dropdown-menu__link"
                            href="/request-money"
                          >
                            <span className="dropdown-menu__icon">
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                viewBox="0 0 32 33"
                                fill="none"
                              >
                                <path
                                  d="M11.5625 19.6091C11.5625 21.3291 12.8825 22.7157 14.5225 22.7157H17.8692C19.2958 22.7157 20.4558 21.5024 20.4558 20.0091C20.4558 18.3824 19.7492 17.8091 18.6958 17.4357L13.3225 15.5691C12.2692 15.1957 11.5625 14.6224 11.5625 12.9957C11.5625 11.5024 12.7225 10.2891 14.1492 10.2891H17.4958C19.1358 10.2891 20.4558 11.6757 20.4558 13.3957"
                                  stroke="currentColor"
                                  strokeWidth="2"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                />
                                <path
                                  d="M16 8.5V24.5"
                                  stroke="currentColor"
                                  strokeWidth="2"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                />
                                <path
                                  d="M20.0013 29.8307H12.0013C5.33464 29.8307 2.66797 27.1641 2.66797 20.4974V12.4974C2.66797 5.83073 5.33464 3.16406 12.0013 3.16406H20.0013C26.668 3.16406 29.3346 5.83073 29.3346 12.4974V20.4974C29.3346 27.1641 26.668 29.8307 20.0013 29.8307Z"
                                  stroke="currentColor"
                                  strokeWidth="2"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                />
                              </svg>
                            </span>
                            <div className="dropdown-menu__content">
                              <h6 className="dropdown-menu__title">
                                {trans("Request Money")}
                              </h6>
                              <p className="dropdown-menu__desc">
                                {trans(
                                  "Request for funds from Family, friends and Business Partners.",
                                )}
                              </p>
                            </div>
                          </Link>
                        </li>
                      </ul>
                    </li>
                  </ul>
                </li>
                <li
                  className={`nav-item has-dropdown ${openDropdown === "solutions" ? "show" : ""
                    }`}
                >
                  <a
                    className={`nav-link ${openDropdown === "solutions" ? "show" : ""
                      }`}
                    href="#"
                    role="button"
                    data-bs-toggle="dropdown"
                    aria-expanded={openDropdown === "solutions"}
                    onClick={() => toggleDropdown("solutions")}
                  >
                    <span className="nav-link__text">{trans("Solutions")}</span>

                    <span
                      className="nav-link__icon"
                      style={{ marginTop: "-5px" }}
                    >
                      <svg
                        width="17"
                        height="16"
                        viewBox="0 0 17 16"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M14.1401 5.96875L9.79341 10.3154C9.28008 10.8288 8.44008 10.8288 7.92675 10.3154L3.58008 5.96875"
                          stroke="#212121"
                          strokeMiterlimit="10"
                          stroke-linecap="round"
                          stroke-linejoin="round"
                        />
                      </svg>
                    </span>
                  </a>
                  <ul
                    className={`dropdown-menu ${openDropdown === "solutions" ? "show" : ""
                      }`}
                    data-bs-popper={
                      openDropdown === "solutions" ? "static" : undefined
                    }
                  >
                    <span className="dropdown-menu__clip-path-shape">
                      <svg
                        viewBox="0 0 30 22"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M12.6 1.2C13.8 -0.4 16.2 -0.4 17.4 1.2L29.4 17.2C30.8833 19.1777 29.4721 22 27 22H3C0.527865 22 -0.883281 19.1777 0.600001 17.2L12.6 1.2Z"
                          fill="currentColor"
                        />
                      </svg>
                    </span>
                    <li className="dropdown-menu__item">
                      <Link
                        className="dropdown-menu__link"
                        href="/payout-solutions"
                      >
                        <span className="dropdown-menu__icon">
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 32 32"
                            fill="none"
                          >
                            <path
                              d="M12.0013 29.3307H20.0013C26.668 29.3307 29.3346 26.6641 29.3346 19.9974V11.9974C29.3346 5.33073 26.668 2.66406 20.0013 2.66406H12.0013C5.33464 2.66406 2.66797 5.33073 2.66797 11.9974V19.9974C2.66797 26.6641 5.33464 29.3307 12.0013 29.3307Z"
                              stroke="CurrentColor"
                              strokeWidth="1.5"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                            <path
                              d="M12 12.6797L16 8.67969L20 12.6797"
                              stroke="CurrentColor"
                              strokeWidth="1.5"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                            <path
                              d="M16 8.67969V19.3464"
                              stroke="CurrentColor"
                              strokeWidth="1.5"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                            <path
                              d="M8 22.0156C13.1867 23.749 18.8133 23.749 24 22.0156"
                              stroke="currentColor"
                              strokeWidth="1.5"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                        </span>
                        <div className="dropdown-menu__content">
                          <h6 className="dropdown-menu__title">
                            {trans("Payout Solutions")}
                          </h6>
                          <p className="dropdown-menu__desc">
                            {trans("Explore Belyfted's payout solutions")}
                          </p>
                        </div>
                      </Link>
                    </li>
                    <li className="dropdown-menu__item">
                      <Link
                        className="dropdown-menu__link"
                        href="/open-banking"
                      >
                        <span className="dropdown-menu__icon">
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 32 32"
                            fill="none"
                          >
                            <path
                              d="M16.4946 2.86906L28.4946 7.66903C28.9613 7.8557 29.3346 8.41569 29.3346 8.90902V13.3357C29.3346 14.069 28.7346 14.669 28.0013 14.669H4.0013C3.26797 14.669 2.66797 14.069 2.66797 13.3357V8.90902C2.66797 8.41569 3.04131 7.8557 3.50798 7.66903L15.508 2.86906C15.7746 2.7624 16.228 2.7624 16.4946 2.86906Z"
                              stroke="currentColor"
                              strokeWidth="1.5"
                              strokeMiterlimit="10"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                            <path
                              d="M29.3346 29.3333H2.66797V25.3333C2.66797 24.6 3.26797 24 4.0013 24H28.0013C28.7346 24 29.3346 24.6 29.3346 25.3333V29.3333Z"
                              stroke="currentColor"
                              strokeWidth="1.5"
                              strokeMiterlimit="10"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                            <path
                              d="M5.33203 23.9974V14.6641"
                              stroke="currentColor"
                              strokeWidth="1.5"
                              strokeMiterlimit="10"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                            <path
                              d="M10.668 23.9974V14.6641"
                              stroke="currentColor"
                              strokeWidth="1.5"
                              strokeMiterlimit="10"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                            <path
                              d="M16 23.9974V14.6641"
                              stroke="currentColor"
                              strokeWidth="1.5"
                              strokeMiterlimit="10"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                            <path
                              d="M21.332 23.9974V14.6641"
                              stroke="currentColor"
                              strokeWidth="1.5"
                              strokeMiterlimit="10"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                            <path
                              d="M26.668 23.9974V14.6641"
                              stroke="currentColor"
                              strokeWidth="1.5"
                              strokeMiterlimit="10"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                            <path
                              d="M1.33203 29.3359H30.6654"
                              stroke="currentColor"
                              strokeWidth="1.5"
                              strokeMiterlimit="10"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                            <path
                              d="M16 11.3359C17.1046 11.3359 18 10.4405 18 9.33594C18 8.23137 17.1046 7.33594 16 7.33594C14.8954 7.33594 14 8.23137 14 9.33594C14 10.4405 14.8954 11.3359 16 11.3359Z"
                              stroke="currentColor"
                              strokeWidth="1.5"
                              strokeMiterlimit="10"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                        </span>
                        <div className="dropdown-menu__content">
                          <h6 className="dropdown-menu__title">
                            {trans("Open Banking")}
                          </h6>
                          <p className="dropdown-menu__desc">
                            {trans("Make hassle-free payments in fewer steps")}
                          </p>
                        </div>
                      </Link>
                    </li>
                  </ul>
                </li>
                <li
                  className={`nav-item has-dropdown ${openDropdown === "company" ? "show" : ""
                    }`}
                >
                  <a
                    className={`nav-link ${openDropdown === "company" ? "show" : ""
                      }`}
                    href="#"
                    role="button"
                    data-bs-toggle="dropdown"
                    aria-expanded={openDropdown === "company"}
                    onClick={() => toggleDropdown("company")}
                  >
                    <span className="nav-link__text">{trans("Company")}</span>

                    <span
                      className="nav-link__icon"
                      style={{ marginTop: "-5px" }}
                    >
                      <svg
                        width="17"
                        height="16"
                        viewBox="0 0 17 16"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M14.1401 5.96875L9.79341 10.3154C9.28008 10.8288 8.44008 10.8288 7.92675 10.3154L3.58008 5.96875"
                          stroke="#212121"
                          strokeMiterlimit="10"
                          stroke-linecap="round"
                          stroke-linejoin="round"
                        />
                      </svg>
                    </span>
                  </a>
                  <ul
                    className={`dropdown-menu mega-menu ${openDropdown === "company" ? "show" : ""
                      }`}
                    data-bs-popper={
                      openDropdown === "company" ? "static" : undefined
                    }
                  >
                    <span className="dropdown-menu__clip-path-shape">
                      <svg
                        viewBox="0 0 30 22"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M12.6 1.2C13.8 -0.4 16.2 -0.4 17.4 1.2L29.4 17.2C30.8833 19.1777 29.4721 22 27 22H3C0.527865 22 -0.883281 19.1777 0.600001 17.2L12.6 1.2Z"
                          fill="currentColor"
                        />
                      </svg>
                    </span>
                    <li className="dropdown-menu__item">
                      <Link
                        className="dropdown-menu__link"
                        href="/pages/about-us"
                      >
                        <span className="dropdown-menu__icon">
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 37 37"
                            fill="none"
                          >
                            <path
                              d="M18.6833 20.8557C18.6524 20.8557 18.6062 20.8557 18.5754 20.8557C18.5291 20.8557 18.4674 20.8557 18.4212 20.8557C14.9216 20.7478 12.3008 18.0191 12.3008 14.6582C12.3008 11.2357 15.0912 8.44531 18.5137 8.44531C21.9362 8.44531 24.7266 11.2357 24.7266 14.6582C24.7112 18.0345 22.0749 20.7478 18.7295 20.8557C18.6987 20.8557 18.6987 20.8557 18.6833 20.8557ZM18.4983 10.7424C16.3399 10.7424 14.5979 12.4999 14.5979 14.6428C14.5979 16.7549 16.2474 18.4661 18.3441 18.5432C18.3904 18.5278 18.5445 18.5278 18.6987 18.5432C20.7645 18.4353 22.3833 16.7395 22.3987 14.6428C22.3987 12.4999 20.6566 10.7424 18.4983 10.7424Z"
                              fill="currentColor"
                            />
                            <path
                              d="M18.5009 35.0744C14.3538 35.0744 10.3918 33.5327 7.32384 30.7269C7.04634 30.4802 6.92301 30.1102 6.95384 29.7556C7.15426 27.921 8.29509 26.2098 10.1913 24.9456C14.7855 21.8931 22.2318 21.8931 26.8105 24.9456C28.7068 26.2252 29.8476 27.921 30.048 29.7556C30.0943 30.1256 29.9555 30.4802 29.678 30.7269C26.6101 33.5327 22.648 35.0744 18.5009 35.0744ZM9.37426 29.4473C11.9334 31.5902 15.1555 32.7619 18.5009 32.7619C21.8463 32.7619 25.0684 31.5902 27.6276 29.4473C27.3501 28.5069 26.6101 27.5973 25.5155 26.8573C21.723 24.329 15.2943 24.329 11.4709 26.8573C10.3763 27.5973 9.65176 28.5069 9.37426 29.4473Z"
                              fill="currentColor"
                            />
                            <path
                              d="M18.4987 35.0755C9.35662 35.0755 1.92578 27.6447 1.92578 18.5026C1.92578 9.36052 9.35662 1.92969 18.4987 1.92969C27.6408 1.92969 35.0716 9.36052 35.0716 18.5026C35.0716 27.6447 27.6408 35.0755 18.4987 35.0755ZM18.4987 4.24219C10.6362 4.24219 4.23828 10.6401 4.23828 18.5026C4.23828 26.3651 10.6362 32.763 18.4987 32.763C26.3612 32.763 32.7591 26.3651 32.7591 18.5026C32.7591 10.6401 26.3612 4.24219 18.4987 4.24219Z"
                              fill="currentColor"
                            />
                          </svg>
                        </span>
                        <div className="dropdown-menu__content">
                          <h6 className="dropdown-menu__title">
                            {trans("About Us")}
                          </h6>
                          <p className="dropdown-menu__desc">
                            {trans("Learn more about Belyfted")}
                          </p>
                        </div>
                      </Link>
                    </li>
                    <li className="dropdown-menu__item">
                      <Link className="dropdown-menu__link" href="/blogs">
                        <span className="dropdown-menu__icon">
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 37 37"
                            fill="none"
                          >
                            <path
                              d="M18.4987 34.0433C18.0362 34.0433 17.5737 33.9354 17.1883 33.7196C14.3054 32.1471 9.23328 30.4821 6.05745 30.0658L5.61036 30.0042C3.59078 29.7575 1.92578 27.8612 1.92578 25.8108V7.18749C1.92578 5.96958 2.4037 4.85958 3.28245 4.05791C4.1612 3.25625 5.30203 2.87083 6.50453 2.97874C9.8962 3.24083 15.0145 4.93666 17.9129 6.75583L18.2829 6.97166C18.3908 7.03333 18.622 7.03333 18.7145 6.98708L18.9612 6.83291C21.8595 5.01374 26.9779 3.28708 30.3849 2.99416C30.4158 2.99416 30.5391 2.99416 30.5699 2.99416C31.6954 2.88624 32.8516 3.28708 33.715 4.08875C34.5937 4.89041 35.0716 6.00041 35.0716 7.21833V25.8262C35.0716 27.8921 33.4066 29.7729 31.3716 30.0196L30.8629 30.0812C27.687 30.4975 22.5995 32.1779 19.7783 33.735C19.4083 33.9508 18.9612 34.0433 18.4987 34.0433ZM6.13453 5.27583C5.6412 5.27583 5.19411 5.44541 4.83953 5.76916C4.45411 6.12375 4.23828 6.63249 4.23828 7.18749V25.8108C4.23828 26.7204 5.02453 27.5992 5.90328 27.7225L6.36578 27.7842C9.83453 28.2467 15.1533 29.9887 18.2366 31.6692C18.3754 31.7308 18.5758 31.7462 18.6529 31.7154C21.7362 30.0042 27.0858 28.2467 30.5699 27.7842L31.0941 27.7225C31.9729 27.6146 32.7591 26.7204 32.7591 25.8108V7.20291C32.7591 6.63249 32.5433 6.13916 32.1579 5.76916C31.757 5.41458 31.2483 5.245 30.6779 5.27583C30.647 5.27583 30.5237 5.27583 30.4929 5.27583C27.5483 5.53791 22.7999 7.12583 20.2099 8.74458L19.9633 8.91416C19.1154 9.43833 17.9129 9.43833 17.0958 8.92958L16.7258 8.71375C14.0895 7.095 9.3412 5.5225 6.31953 5.27583C6.25786 5.27583 6.1962 5.27583 6.13453 5.27583Z"
                              fill="currentColor"
                            />
                            <path
                              d="M18.5 32.7422C17.8679 32.7422 17.3438 32.218 17.3438 31.5859V8.46094C17.3438 7.82885 17.8679 7.30469 18.5 7.30469C19.1321 7.30469 19.6562 7.82885 19.6562 8.46094V31.5859C19.6562 32.2334 19.1321 32.7422 18.5 32.7422Z"
                              fill="currentColor"
                            />
                            <path
                              d="M11.9492 14.2422H8.48047C7.84839 14.2422 7.32422 13.718 7.32422 13.0859C7.32422 12.4539 7.84839 11.9297 8.48047 11.9297H11.9492C12.5813 11.9297 13.1055 12.4539 13.1055 13.0859C13.1055 13.718 12.5813 14.2422 11.9492 14.2422Z"
                              fill="currentColor"
                            />
                            <path
                              d="M13.1055 18.8672H8.48047C7.84839 18.8672 7.32422 18.343 7.32422 17.7109C7.32422 17.0789 7.84839 16.5547 8.48047 16.5547H13.1055C13.7376 16.5547 14.2617 17.0789 14.2617 17.7109C14.2617 18.343 13.7376 18.8672 13.1055 18.8672Z"
                              fill="currentColor"
                            />
                          </svg>
                        </span>
                        <div className="dropdown-menu__content">
                          <h6 className="dropdown-menu__title">
                            {trans("Blog")}
                          </h6>
                          <p className="dropdown-menu__desc">
                            {trans("Read the latest insights and updates")}
                          </p>
                        </div>
                      </Link>
                    </li>
                    <li className="dropdown-menu__item">
                      <Link className="dropdown-menu__link" href="/careers">
                        <span className="dropdown-menu__icon">
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 37 37"
                            fill="none"
                          >
                            <path
                              d="M24.6661 35.0731H12.3328C5.21029 35.0731 3.88446 31.7585 3.54529 28.5365L2.38904 16.1877C2.21946 14.569 2.17321 12.1794 3.77654 10.391C5.16404 8.84937 7.46112 8.10938 10.7911 8.10938H26.2078C29.5532 8.10938 31.8503 8.86479 33.2224 10.391C34.8257 12.1794 34.7795 14.569 34.6099 16.2031L33.4536 28.521C33.1145 31.7585 31.7886 35.0731 24.6661 35.0731ZM10.7911 10.4065C8.18571 10.4065 6.39737 10.9152 5.48779 11.9327C4.73237 12.7652 4.48571 14.0448 4.68612 15.9565L5.84237 28.3052C6.10446 30.741 6.76737 32.7606 12.3328 32.7606H24.6661C30.2161 32.7606 30.8945 30.741 31.1565 28.2898L32.3128 15.9719C32.5132 14.0448 32.2665 12.7652 31.5111 11.9327C30.6015 10.9152 28.8132 10.4065 26.2078 10.4065H10.7911Z"
                              fill="currentColor"
                            />
                            <path
                              d="M24.6654 10.4089C24.0333 10.4089 23.5091 9.88469 23.5091 9.2526V8.01927C23.5091 5.2751 23.5091 4.24219 19.732 4.24219H17.2654C13.4883 4.24219 13.4883 5.2751 13.4883 8.01927V9.2526C13.4883 9.88469 12.9641 10.4089 12.332 10.4089C11.6999 10.4089 11.1758 9.88469 11.1758 9.2526V8.01927C11.1758 5.30594 11.1758 1.92969 17.2654 1.92969H19.732C25.8216 1.92969 25.8216 5.30594 25.8216 8.01927V9.2526C25.8216 9.88469 25.2974 10.4089 24.6654 10.4089Z"
                              fill="currentColor"
                            />
                            <path
                              d="M18.5013 25.8229C14.2617 25.8229 14.2617 23.2021 14.2617 21.6296V20.0417C14.2617 17.8679 14.7859 17.3438 16.9596 17.3438H20.043C22.2167 17.3438 22.7409 17.8679 22.7409 20.0417V21.5833C22.7409 23.1867 22.7409 25.8229 18.5013 25.8229ZM16.5742 19.6562C16.5742 19.7796 16.5742 19.9183 16.5742 20.0417V21.6296C16.5742 23.2175 16.5742 23.5104 18.5013 23.5104C20.4284 23.5104 20.4284 23.2638 20.4284 21.6142V20.0417C20.4284 19.9183 20.4284 19.7796 20.4284 19.6562C20.3051 19.6562 20.1663 19.6562 20.043 19.6562H16.9596C16.8363 19.6562 16.6976 19.6562 16.5742 19.6562Z"
                              fill="currentColor"
                            />
                            <path
                              d="M21.5831 22.7668C21.0127 22.7668 20.5039 22.3351 20.4422 21.7493C20.3652 21.1172 20.8122 20.5314 21.4443 20.4543C25.5143 19.9455 29.4147 18.4039 32.6985 16.0143C33.2072 15.6289 33.9318 15.7522 34.3172 16.2764C34.6872 16.7851 34.5793 17.5097 34.0552 17.8951C30.4477 20.516 26.1927 22.1964 21.7218 22.7668C21.6756 22.7668 21.6293 22.7668 21.5831 22.7668Z"
                              fill="currentColor"
                            />
                            <path
                              d="M15.4163 22.7823C15.3701 22.7823 15.3238 22.7823 15.2776 22.7823C11.0534 22.3044 6.93715 20.7627 3.3759 18.3269C2.85173 17.9723 2.71298 17.2477 3.06757 16.7236C3.42215 16.1994 4.14673 16.0606 4.6709 16.4152C7.92382 18.6352 11.6701 20.0381 15.5242 20.4852C16.1563 20.5623 16.6188 21.1327 16.5417 21.7648C16.4955 22.3506 16.0022 22.7823 15.4163 22.7823Z"
                              fill="currentColor"
                            />
                          </svg>
                        </span>
                        <div className="dropdown-menu__content">
                          <h6 className="dropdown-menu__title">
                            {trans("Career")}
                          </h6>
                          <p className="dropdown-menu__desc">
                            {trans("Join our team and grow with us")}
                          </p>
                        </div>
                      </Link>
                    </li>
                    <li className="dropdown-menu__item">
                      <Link
                        className="dropdown-menu__link"
                        href="/pages/code-of-conduct"
                      >
                        <span className="dropdown-menu__icon">
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 32 33"
                            fill="none"
                          >
                            <path
                              d="M28 9.83073V23.1641C28 27.1641 26 29.8307 21.3333 29.8307H10.6667C6 29.8307 4 27.1641 4 23.1641V9.83073C4 5.83073 6 3.16406 10.6667 3.16406H21.3333C26 3.16406 28 5.83073 28 9.83073Z"
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeMiterlimit="10"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                            <path
                              d="M20.6654 3.16406V13.644C20.6654 14.2307 19.972 14.524 19.5453 14.1374L16.4521 11.2841C16.1987 11.0441 15.7987 11.0441 15.5453 11.2841L12.4521 14.1374C12.0254 14.524 11.332 14.2307 11.332 13.644V3.16406H20.6654Z"
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeMiterlimit="10"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                            <path
                              d="M17.668 19.1641H23.3346"
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeMiterlimit="10"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                            <path
                              d="M12 24.5H23.3333"
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeMiterlimit="10"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                        </span>
                        <div className="dropdown-menu__content">
                          <h6 className="dropdown-menu__title">
                            {trans("Code of Conduct")}
                          </h6>
                          <p className="dropdown-menu__desc">
                            {trans("Learn more about our code of conduct")}
                          </p>
                        </div>
                      </Link>
                    </li>

                    {customPageData?.data?.pages?.map((page) => (
                      <li key={page.id} className="dropdown-menu__item">
                        <Link
                          href={`/pages/${page.slug}`}
                          className="dropdown-menu__link"
                          onClick={() => {
                            setIsMenuOpen(false);
                            setOpenDropdown(null);
                          }}
                        >
                          <span
                            className="dropdown-menu__icon"
                            dangerouslySetInnerHTML={{
                              __html: page.svg,
                            }}
                          ></span>
                          <div className="dropdown-menu__content">
                            <h6 className="dropdown-menu__title">
                              <span className="dropdown-menu__title-text">
                                {page.name}
                              </span>
                              <span className="dropdown-menu__title-icon">
                                <svg
                                  xmlns="http://www.w3.org/2000/svg"
                                  viewBox="0 0 17 16"
                                  fill="none"
                                >
                                  <path
                                    d="M1.13281 7.26562C0.580528 7.26562 0.132813 7.71334 0.132812 8.26562C0.132812 8.81791 0.580528 9.26562 1.13281 9.26562L1.13281 7.26562ZM16.7059 8.97273C17.0964 8.58221 17.0964 7.94904 16.7059 7.55852L10.3419 1.19456C9.95139 0.804033 9.31823 0.804033 8.9277 1.19456C8.53718 1.58508 8.53718 2.21825 8.9277 2.60877L14.5846 8.26563L8.9277 13.9225C8.53718 14.313 8.53718 14.9462 8.9277 15.3367C9.31823 15.7272 9.95139 15.7272 10.3419 15.3367L16.7059 8.97273ZM1.13281 9.26562L15.9988 9.26563L15.9988 7.26563L1.13281 7.26562L1.13281 9.26562Z"
                                    fill="currentColor"
                                  />
                                </svg>
                              </span>
                            </h6>
                            <p className="dropdown-menu__desc">
                              {page.short_text}
                            </p>
                          </div>
                        </Link>
                      </li>
                    ))}
                  </ul>
                </li>
                <li
                  className={`nav-item has-dropdown ${openDropdown === "help" ? "show" : ""
                    }`}
                >
                  <a
                    className={`nav-link ${openDropdown === "help" ? "show" : ""}`}
                    href="#"
                    role="button"
                    data-bs-toggle="dropdown"
                    aria-expanded={openDropdown === "help"}
                    onClick={() => toggleDropdown("help")}
                  >
                    <span className="nav-link__text">{trans("Help")}</span>

                    <span
                      className="nav-link__icon"
                      style={{ marginTop: "-5px" }}
                    >
                      <svg
                        width="17"
                        height="16"
                        viewBox="0 0 17 16"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M14.1401 5.96875L9.79341 10.3154C9.28008 10.8288 8.44008 10.8288 7.92675 10.3154L3.58008 5.96875"
                          stroke="#212121"
                          strokeMiterlimit="10"
                          stroke-linecap="round"
                          stroke-linejoin="round"
                        />
                      </svg>
                    </span>
                  </a>
                  <ul
                    className={`dropdown-menu ${openDropdown === "help" ? "show" : ""
                      }`}
                    data-bs-popper={
                      openDropdown === "help" ? "static" : undefined
                    }
                  >
                    <span className="dropdown-menu__clip-path-shape">
                      <svg
                        viewBox="0 0 30 22"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M12.6 1.2C13.8 -0.4 16.2 -0.4 17.4 1.2L29.4 17.2C30.8833 19.1777 29.4721 22 27 22H3C0.527865 22 -0.883281 19.1777 0.600001 17.2L12.6 1.2Z"
                          fill="currentColor"
                        />
                      </svg>
                    </span>
                    <li className="dropdown-menu__item">
                      <Link className="dropdown-menu__link" href="/help/faq">
                        <span className="dropdown-menu__icon">
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 32 32"
                            fill="none"
                          >
                            <path
                              d="M22.668 24.5756H17.3346L11.4013 28.5222C10.5213 29.1089 9.33464 28.4823 9.33464 27.4156V24.5756C5.33464 24.5756 2.66797 21.9089 2.66797 17.9089V9.90885C2.66797 5.90885 5.33464 3.24219 9.33464 3.24219H22.668C26.668 3.24219 29.3346 5.90885 29.3346 9.90885V17.9089C29.3346 21.9089 26.668 24.5756 22.668 24.5756Z"
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeMiterlimit="10"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                            <path
                              d="M15.9985 15.1484V14.8685C15.9985 13.9618 16.5585 13.4818 17.1185 13.0951C17.6652 12.7218 18.2118 12.2418 18.2118 11.3618C18.2118 10.1351 17.2251 9.14844 15.9985 9.14844C14.7718 9.14844 13.7852 10.1351 13.7852 11.3618"
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                            <path
                              d="M15.9927 18.3307H16.0047"
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                        </span>
                        <div className="dropdown-menu__content">
                          <h6 className="dropdown-menu__title">
                            {trans("FAQ")}
                          </h6>
                          <p className="dropdown-menu__desc">
                            {trans("Learn more about Belyfted")}
                          </p>
                        </div>
                      </Link>
                    </li>
                    <li className="dropdown-menu__item">
                      <Link
                        className="dropdown-menu__link"
                        href="/help/contact"
                      >
                        <span className="dropdown-menu__icon">
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 32 32"
                            fill="none"
                          >
                            <path
                              d="M24 25.1463H22.9867C21.92 25.1463 20.9067 25.5596 20.16 26.3063L17.88 28.5597C16.84 29.5864 15.1467 29.5864 14.1067 28.5597L11.8267 26.3063C11.08 25.5596 10.0533 25.1463 9 25.1463H8C5.78667 25.1463 4 23.373 4 21.1864V6.63965C4 4.45298 5.78667 2.67969 8 2.67969H24C26.2133 2.67969 28 4.45298 28 6.63965V21.1864C28 23.3597 26.2133 25.1463 24 25.1463Z"
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeMiterlimit="10"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                            <path
                              d="M9.33594 12.2065C9.33594 10.9665 10.3493 9.95312 11.5893 9.95312C12.8293 9.95312 13.8426 10.9665 13.8426 12.2065C13.8426 14.7132 10.2826 14.9799 9.49593 17.3665C9.33593 17.8599 9.74927 18.3532 10.2693 18.3532H13.8426"
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                            <path
                              d="M21.3903 18.3426V10.7293C21.3903 10.3826 21.1636 10.0759 20.8303 9.98256C20.4969 9.88923 20.1369 10.0226 19.9503 10.3159C18.9903 11.8626 17.9503 13.6226 17.0436 15.1692C16.8969 15.4226 16.8969 15.7559 17.0436 16.0093C17.1903 16.2626 17.4703 16.4225 17.7769 16.4225H22.6703"
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                        </span>
                        <div className="dropdown-menu__content">
                          <h6 className="dropdown-menu__title">
                            {trans("Contact us")}
                          </h6>
                          <p className="dropdown-menu__desc">
                            {trans("Reach out to us via our various channels")}
                          </p>
                        </div>
                      </Link>
                    </li>
                  </ul>
                </li>
                <li className="nav-item buttons ms-auto">
                  <Link
                    className="link"
                    href="/login"
                    style={{ fontSize: "16px", color: "#042656" }}
                  >
                    {isLoggedIn ? "Dashboard" : "Log in"}
                  </Link>
                  <Link
                    className="btn btn--warning btn--sm"
                    style={{ paddingTop: "16px", paddingBottom: "16px" }}
                    href="/register/step-1"
                  >
                    {trans("Join Belyfted")}
                  </Link>
                </li>

                {/* Auth Buttons (Mobile) */}
                <li className="nav-item d-block d-lg-none text-center mt-2">
                  {isLoggedIn ? (
                    <Link
                      className="btn btn--base w-100 mb-2 text-sm-nav"
                      href="/user/dashboard"
                    >
                      {trans("Dashboard")}
                    </Link>
                  ) : (
                    <>
                      <Link
                        className="btn btn--base w-100 mb-2 text-sm-nav"
                        href="/login"
                      >
                        {trans("Log In")}
                      </Link>
                      <Link
                        className="btn btn--warning w-100 text-sm-nav"
                        href="/register/step-1"
                      >
                        {trans("Join Belyfted")}
                      </Link>
                    </>
                  )}
                </li>
              </ul>
            </div>
          </nav>
        </div>
      </header>
    </>
  );
}

function NavLinks({ links, trans }) {
  return (
    <>
      {links.map((link, index) => (
        <li key={index} className="nav-item">
          <Link
            href={link.url}
            className={`nav-link ${link.active ? "active" : ""}`}
          >
            {trans(link.title)}
          </Link>
        </li>
      ))}
    </>
  );
}

function CustomPageLinks({ pages, pathname, trans }) {
  return (
    <>
      {pages?.map((page, index) => (
        <li key={index} className="nav-item">
          <Link
            href={`/${page.slug}`}
            className={`nav-link ${pathname === "/" + page.slug ? "active" : ""
              }`}
          >
            {trans(page.name)}
          </Link>
        </li>
      ))}
    </>
  );
}
